import { useState, useCallback } from 'react';

import { deepCopy } from '../utils';

function parsePathKeys(path: string): string[] {
  const keys: string[] = [];
  let current = '';
  let inBracket = false;

  for (let i = 0; i < path.length; i++) {
    const char = path[i];

    if (char === '[') {
      if (current) {
        keys.push(current);
        current = '';
      }
      inBracket = true;
    } else if (char === ']') {
      if (inBracket && current) {
        keys.push(current);
        current = '';
      }
      inBracket = false;
    } else if (char === '.' && !inBracket) {
      if (current) {
        keys.push(current);
        current = '';
      }
    } else {
      current += char;
    }
  }

  if (current) {
    keys.push(current);
  }

  return keys;
}

function extractNestedValue(obj: any, path: string): any {
  const keys = parsePathKeys(path);
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    if (current === null || current === undefined) return undefined;
    current = current[keys[i]];
  }

  return current;
}

function updateNestedValue(obj: any, path: string, newValue: any): any {
  const keys = parsePathKeys(path);
  const cloned = deepCopy(obj);

  let current = cloned;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] === null || current[key] === undefined) {
      // Check if next key is numeric to determine if we need array or object
      const nextKey = keys[i + 1];
      current[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = newValue;
  return cloned;
}

export function useNestedInput<T extends Record<string, any>>(initialValue: T) {
  const [data, setData] = useState<T>(initialValue);

  const updateValue = useCallback((path: string, value: any) => {
    setData((prev) => updateNestedValue(prev, path, value));
  }, []);

  const bind = useCallback(
    (path: string) => ({
      value: extractNestedValue(data, path) ?? '',
      name: path,
      onChange: (event: any) => {
        updateValue(path, event.target.value);
      },
      setValue: (val: any) => updateValue(path, val),
    }),
    [data, updateValue],
  );

  const bindCheckbox = useCallback(
    (path: string) => ({
      checked: !!extractNestedValue(data, path),
      name: path,
      onChange: (event: any) => {
        updateValue(path, event.target.checked);
      },
      setChecked: (val: boolean) => updateValue(path, val),
    }),
    [data, updateValue],
  );

  return {
    data,
    setData,
    reset: () => setData(initialValue),
    bind,
    bindCheckbox,
  };
}
